/**
 * Dance type / category helpers (frontend_v2).
 *
 * Prefer explicit `event.categories` when present (manual / future sources).
 * Otherwise keep source-based and keyword inference for scraped events.
 */

const WORD_RE = {
  salsa: /\bsalsa\b/i,
  bachata: /\bbachata\b/i,
};

function toLowerSafe(value) {
  return (typeof value === 'string' ? value : '').toLowerCase();
}

/**
 * Normalize explicit categories from an event.
 * @returns {string[]}
 */
export function getExplicitCategories(event) {
  const raw = event?.categories;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => (typeof c === 'string' ? c.trim() : ''))
    .filter(Boolean);
}

/**
 * Infer Salsa/Bachata tags for SalsaVida events.
 */
export function inferSalsaVidaTags(event) {
  const eventName = toLowerSafe(event?.['Event Name']);
  const description = toLowerSafe(event?.Description);

  const titleHasSalsa = WORD_RE.salsa.test(eventName);
  const titleHasBachata = WORD_RE.bachata.test(eventName);

  if (titleHasSalsa || titleHasBachata) {
    const tags = new Set();
    if (titleHasSalsa) tags.add('Salsa');
    if (titleHasBachata) tags.add('Bachata');
    return tags;
  }

  const descHasSalsa = WORD_RE.salsa.test(description);
  const descHasBachata = WORD_RE.bachata.test(description);

  if (descHasSalsa || descHasBachata) {
    const tags = new Set();
    if (descHasSalsa) tags.add('Salsa');
    if (descHasBachata) tags.add('Bachata');
    return tags;
  }

  return new Set(['Salsa']);
}

export function getSalsaVidaPrimaryTag(tags) {
  if (!tags || !(tags instanceof Set)) return 'Salsa';
  if (tags.has('Bachata') && !tags.has('Salsa')) return 'Bachata';
  return 'Salsa';
}

/**
 * Map a display label to an event-card / detail CSS suffix.
 * Unknown non-empty labels fall back to `fusion` so cards still get a border.
 */
export function tagLabelToClassSuffix(label) {
  switch (label) {
    case 'Tango':
      return 'tango';
    case 'Fusion':
    case 'Dance Show':
    case 'Dance':
    case 'Contemporary Ballet':
    case 'Ballet':
    case 'Other':
      return 'fusion';
    case 'Salsa':
      return 'salsa';
    case 'Bachata':
      return 'bachata';
    case 'West Coast Swing':
      return 'wcs';
    case 'Brazilian Zouk':
    case 'Zouk':
    case 'Meditation':
      return 'zouk';
    case 'Contact Improvisation':
    case 'Improvisation':
    case 'Ecstatic Dance':
    case 'Yoga':
      return 'contact';
    case 'Ballroom':
    case 'East Coast Swing':
    case 'Swing':
      return 'wcs';
    default:
      return label ? 'fusion' : '';
  }
}

/**
 * Infer a single display type from source / keywords (no explicit categories).
 */
export function inferDisplayTypeFromSourceOrKeywords(event) {
  const eventName = toLowerSafe(event?.['Event Name']);
  const description = toLowerSafe(event?.Description);
  const textToSearch = `${eventName} ${description}`;
  const source = event?.source || '';

  if (source === 'tangomango') return 'Tango';
  if (source === 'bayareafusion') return 'Fusion';
  if (source === 'salsavida') return getSalsaVidaPrimaryTag(inferSalsaVidaTags(event));
  if (source.startsWith('googlecalendar')) return 'Contact Improvisation';
  if (source.startsWith('nextgenswing')) return 'West Coast Swing';
  if (source === 'omni_bachata') return 'Bachata';
  if (source === 'omni_zouk' || source === 'bay_area_zouk') return 'Brazilian Zouk';

  if (
    (eventName.includes('meditation') ||
      textToSearch.includes('meditation class') ||
      (textToSearch.includes('meditation') && !textToSearch.includes('meditative movement'))) &&
    !textToSearch.includes('yoga') &&
    !textToSearch.includes('vinyasa')
  ) {
    return 'Meditation';
  }
  if (
    (eventName.includes('yoga') ||
      textToSearch.includes('yoga class') ||
      textToSearch.includes('vinyasa')) &&
    !eventName.startsWith('meditation')
  ) {
    return 'Yoga';
  }
  if (
    textToSearch.includes('fusion') &&
    !textToSearch.includes('milonga') &&
    !textToSearch.includes('tango') &&
    !textToSearch.includes('practica') &&
    !textToSearch.includes('milonguera')
  ) {
    return 'Fusion';
  }
  if (
    textToSearch.includes('milonga') ||
    textToSearch.includes('tango') ||
    textToSearch.includes('practica') ||
    textToSearch.includes('milonguera')
  ) {
    return 'Tango';
  }
  if (textToSearch.includes('ecstatic dance')) {
    return 'Ecstatic Dance';
  }
  if (textToSearch.includes('west coast swing') || textToSearch.includes('wcs')) {
    return 'West Coast Swing';
  }
  if (
    textToSearch.includes('dance') &&
    !textToSearch.includes('tango') &&
    !textToSearch.includes('milonga') &&
    !textToSearch.includes('practica') &&
    !textToSearch.includes('milonguera') &&
    !textToSearch.includes('ecstatic dance') &&
    !textToSearch.includes('fusion') &&
    !eventName.includes('yoga') &&
    !textToSearch.includes('yoga class') &&
    !eventName.includes('meditation') &&
    !textToSearch.includes('meditation class')
  ) {
    return 'Dance Show';
  }
  return null;
}

/**
 * Tags shown on cards / detail. Prefer explicit categories.
 */
export function getEventDisplayTags(event) {
  const explicit = getExplicitCategories(event);
  if (explicit.length) return explicit;

  const source = event?.source || '';
  if (source === 'salsavida') return Array.from(inferSalsaVidaTags(event));

  const inferred = inferDisplayTypeFromSourceOrKeywords(event);
  return inferred ? [inferred] : [];
}

export function getEventPrimaryDisplayType(event) {
  const tags = getEventDisplayTags(event);
  return tags[0] || null;
}

export function getEventTypeClassSuffix(event, activeFilter) {
  const source = event?.source || '';
  if (source === 'salsavida' && getExplicitCategories(event).length === 0) {
    const tags = inferSalsaVidaTags(event);
    if (activeFilter === 'Salsa' && tags.has('Salsa')) return 'salsa';
    if (activeFilter === 'Bachata' && tags.has('Bachata')) return 'bachata';
    return tagLabelToClassSuffix(getSalsaVidaPrimaryTag(tags)) || 'salsa';
  }

  const primary = getEventPrimaryDisplayType(event);
  return tagLabelToClassSuffix(primary) || '';
}

/**
 * Whether an event matches the dance-type filter dropdown value.
 * Explicit categories win when present.
 */
export function eventMatchesDanceTypeFilter(event, danceTypeFilter) {
  if (!danceTypeFilter || danceTypeFilter === 'All') return true;

  const explicit = getExplicitCategories(event);
  if (explicit.length) {
    return explicit.includes(danceTypeFilter);
  }

  const source = event?.source || '';
  const isKnownSource =
    source === 'tangomango' ||
    source === 'bayareafusion' ||
    source === 'salsavida' ||
    source.startsWith('googlecalendar') ||
    source.startsWith('nextgenswing') ||
    source === 'omni_bachata' ||
    source === 'omni_zouk' ||
    source === 'bay_area_zouk';

  if (danceTypeFilter === 'Tango' && source === 'tangomango') return true;
  if (danceTypeFilter === 'Fusion' && source === 'bayareafusion') return true;
  if (source === 'salsavida') {
    const tags = inferSalsaVidaTags(event);
    if (danceTypeFilter === 'Bachata') return tags.has('Bachata');
    if (danceTypeFilter === 'Salsa') return tags.has('Salsa');
    return false;
  }
  if (
    (danceTypeFilter === 'Improvisation' || danceTypeFilter === 'Contact Improvisation') &&
    source.startsWith('googlecalendar')
  ) {
    return true;
  }
  if (danceTypeFilter === 'West Coast Swing' && source.startsWith('nextgenswing')) return true;
  if (danceTypeFilter === 'Bachata' && source === 'omni_bachata') return true;
  if (
    danceTypeFilter === 'Brazilian Zouk' &&
    (source === 'omni_zouk' || source === 'bay_area_zouk')
  ) {
    return true;
  }

  if (isKnownSource) return false;

  const inferred = inferDisplayTypeFromSourceOrKeywords(event);
  if (!inferred) return false;
  if (inferred === danceTypeFilter) return true;
  // Filter list historically used "Improvisation" while display may say Contact Improvisation
  if (
    danceTypeFilter === 'Improvisation' &&
    (inferred === 'Contact Improvisation' || inferred === 'Improvisation')
  ) {
    return true;
  }
  if (
    danceTypeFilter === 'Contact Improvisation' &&
    (inferred === 'Contact Improvisation' || inferred === 'Improvisation')
  ) {
    return true;
  }
  return false;
}

/**
 * Collect filter dropdown options from a list of events.
 */
export function collectDanceTypeFilterOptions(events) {
  const types = new Set();

  (events || []).forEach((event) => {
    const explicit = getExplicitCategories(event);
    if (explicit.length) {
      explicit.forEach((c) => types.add(c));
      return;
    }

    const source = event?.source || '';
    if (source === 'tangomango') {
      types.add('Tango');
      return;
    }
    if (source === 'bayareafusion') {
      types.add('Fusion');
      return;
    }
    if (source === 'salsavida') {
      inferSalsaVidaTags(event).forEach((t) => types.add(t));
      return;
    }
    if (source.startsWith('googlecalendar')) {
      types.add('Improvisation');
      return;
    }
    if (source.startsWith('nextgenswing')) {
      types.add('West Coast Swing');
      return;
    }
    if (source === 'omni_bachata') {
      types.add('Bachata');
      return;
    }
    if (source === 'omni_zouk' || source === 'bay_area_zouk') {
      types.add('Brazilian Zouk');
      return;
    }

    const inferred = inferDisplayTypeFromSourceOrKeywords(event);
    if (inferred === 'Contact Improvisation') {
      types.add('Improvisation');
    } else if (inferred) {
      types.add(inferred);
    }
  });

  return ['All', ...Array.from(types).sort()];
}

/**
 * Ticket CTA URL: TicketURL only.
 * Details is often a scraper source page, not a box office.
 */
export function getTicketOrEventUrl(event) {
  const value = event?.TicketURL;
  if (typeof value === 'string' && /^https?:\/\//i.test(value.trim())) {
    return value.trim();
  }
  return null;
}
